/**
 * ╔══════════════════════════════════════════════════════════════════════╗
 * ║  USER MANAGEMENT ADMIN PANEL                                         ║
 * ║  Life & Wealth Solutions V33 — Admin Only                           ║
 * ║  Manage: Amir (Support), John, Shawn, Teresa (Sales/Admin)          ║
 * ╚══════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// ── TYPES ────────────────────────────────────────────────────────────────────
interface User {
  user_id: string;
  full_name: string;
  email: string;
  role: 'admin' | 'agent12' | 'viewer';
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  agency_id: string;
  phone: string | null;
  license_number: string | null;
}

interface ModalState {
  type: 'create' | 'edit' | 'reset_password' | 'confirm_deactivate' | 'confirm_delete' | null;
  user: User | null;
}

interface FormData {
  full_name: string;
  email: string;
  role: 'admin' | 'agent12' | 'viewer';
  phone: string;
  license_number: string;
  password: string;
  confirm_password: string;
}

const ROLE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  admin:   { label: 'Admin',    color: '#1B3A6B', bg: '#D6E4F0' },
  agent12: { label: 'Agent 12', color: '#6B3A1B', bg: '#F0E6D6' },
  viewer:  { label: 'Viewer',   color: '#1B6B3A', bg: '#D6F0E4' },
};

const api = axios.create({ baseURL: API });
api.interceptors.request.use((c) => {
  const token = localStorage.getItem('access_token');
  if (token && c.headers) c.headers.Authorization = `Bearer ${token}`;
  return c;
});

// ── HELPERS ───────────────────────────────────────────────────────────────────
function Badge({ role }: { role: string }) {
  const r = ROLE_LABELS[role] || { label: role, color: '#555', bg: '#eee' };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
      fontSize: '12px', fontWeight: 600, color: r.color, background: r.bg,
    }}>{r.label}</span>
  );
}

function StatusDot({ active }: { active: boolean }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: active ? '#1B6B3A' : '#999' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? '#22C55E' : '#D1D5DB', display: 'inline-block' }} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

function formatDate(d: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const EMPTY_FORM: FormData = {
  full_name: '', email: '', role: 'admin', phone: '', license_number: '', password: '', confirm_password: '',
};

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modal, setModal] = useState<ModalState>({ type: null, user: null });
  const [form, setForm] = useState<FormData>(EMPTY_FORM);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [agencyId, setAgencyId] = useState('');

  const showSuccess = (msg: string) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };
  const showError = (msg: string) => { setError(msg); setTimeout(() => setError(''), 6000); };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filterRole) params.role = filterRole;
      if (filterStatus) params.is_active = filterStatus;
      const { data } = await api.get('/users', { params });
      setUsers(data.users || data);
      if (data.users?.length > 0 && !agencyId) setAgencyId(data.users[0].agency_id);
    } catch (e: any) {
      showError(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [filterRole, filterStatus, agencyId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── OPEN MODALS ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setForm(EMPTY_FORM);
    setModal({ type: 'create', user: null });
  };

  const openEdit = (u: User) => {
    setForm({ full_name: u.full_name, email: u.email, role: u.role, phone: u.phone || '', license_number: u.license_number || '', password: '', confirm_password: '' });
    setModal({ type: 'edit', user: u });
  };

  const openResetPassword = (u: User) => {
    setNewPassword('');
    setModal({ type: 'reset_password', user: u });
  };

  const closeModal = () => { setModal({ type: null, user: null }); setForm(EMPTY_FORM); setNewPassword(''); };

  // ── ACTIONS ──────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.full_name || !form.email || !form.password) return showError('Name, email and password are required');
    if (form.password !== form.confirm_password) return showError('Passwords do not match');
    if (!agencyId) return showError('No agency ID found — ensure setup script was run');

    setSaving(true);
    try {
      await api.post('/users', {
        full_name: form.full_name, email: form.email, role: form.role,
        phone: form.phone || null, license_number: form.license_number || null,
        password: form.password, agency_id: agencyId,
      });
      showSuccess(`✅ User ${form.full_name} created successfully`);
      closeModal();
      loadUsers();
    } catch (e: any) {
      showError(e.response?.data?.detail || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const handleEdit = async () => {
    if (!modal.user) return;
    setSaving(true);
    try {
      await api.put(`/users/${modal.user.user_id}`, {
        full_name: form.full_name, role: form.role,
        phone: form.phone || null, license_number: form.license_number || null,
      });
      showSuccess(`✅ ${form.full_name}'s profile updated`);
      closeModal();
      loadUsers();
    } catch (e: any) {
      showError(e.response?.data?.detail || 'Failed to update user');
    } finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!modal.user || !newPassword) return showError('New password is required');
    setSaving(true);
    try {
      await api.post(`/users/${modal.user.user_id}/reset-password`, { new_password: newPassword });
      showSuccess(`✅ Password reset for ${modal.user.full_name}. Share the new password securely.`);
      closeModal();
    } catch (e: any) {
      showError(e.response?.data?.detail || 'Password reset failed');
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (u: User) => {
    try {
      const endpoint = u.is_active ? 'deactivate' : 'activate';
      await api.post(`/users/${u.user_id}/${endpoint}`);
      showSuccess(`✅ ${u.full_name} has been ${u.is_active ? 'deactivated' : 'activated'}`);
      loadUsers();
    } catch (e: any) {
      showError(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDelete = async () => {
    if (!modal.user) return;
    setSaving(true);
    try {
      await api.delete(`/users/${modal.user.user_id}`);
      showSuccess(`✅ ${modal.user.full_name} has been permanently deleted`);
      closeModal();
      loadUsers();
    } catch (e: any) {
      showError(e.response?.data?.detail || 'Failed to delete user');
    } finally { setSaving(false); }
  };

  // ── STYLES ────────────────────────────────────────────────────────────────
  const S = {
    page: { padding: '32px', fontFamily: 'Inter, Arial, sans-serif', background: '#F8FAFC', minHeight: '100vh' } as React.CSSProperties,
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' } as React.CSSProperties,
    title: { fontSize: '26px', fontWeight: 700, color: '#1B3A6B', margin: 0 } as React.CSSProperties,
    subtitle: { fontSize: '14px', color: '#64748B', marginTop: '4px' } as React.CSSProperties,
    btn: (color = '#1B3A6B', light = false) => ({
      background: light ? 'transparent' : color, color: light ? color : '#fff',
      border: light ? `1.5px solid ${color}` : 'none', borderRadius: '8px',
      padding: '9px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
      transition: 'opacity .15s',
    } as React.CSSProperties),
    card: { background: '#fff', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,.08)', overflow: 'hidden' } as React.CSSProperties,
    th: { padding: '12px 16px', fontSize: '12px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'left', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' } as React.CSSProperties,
    td: { padding: '14px 16px', fontSize: '14px', color: '#1E293B', borderBottom: '1px solid #F1F5F9', verticalAlign: 'middle' } as React.CSSProperties,
    input: { width: '100%', padding: '9px 12px', border: '1.5px solid #CBD5E1', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' } as React.CSSProperties,
    label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '6px' } as React.CSSProperties,
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 } as React.CSSProperties,
    modalBox: { background: '#fff', borderRadius: '16px', padding: '32px', width: '480px', maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,.25)', maxHeight: '90vh', overflowY: 'auto' } as React.CSSProperties,
    alert: (ok: boolean) => ({ padding: '12px 16px', borderRadius: '8px', fontSize: '14px', marginBottom: '20px', background: ok ? '#D6F0E4' : '#FDECEA', color: ok ? '#1B6B3A' : '#B91C1C', fontWeight: 500 } as React.CSSProperties),
  };

  const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ marginBottom: '16px' }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* HEADER */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>User Management</h1>
          <p style={S.subtitle}>Manage platform access for Amir, John, Shawn, and Teresa — Admin only</p>
        </div>
        <button style={S.btn()} onClick={openCreate}>+ Add User</button>
      </div>

      {/* ALERTS */}
      {success && <div style={S.alert(true)}>{success}</div>}
      {error && <div style={S.alert(false)}>{error}</div>}

      {/* FILTERS */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select style={{ ...S.input, width: '180px' }} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="agent12">Agent 12</option>
          <option value="viewer">Viewer</option>
        </select>
        <select style={{ ...S.input, width: '180px' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button style={S.btn('#64748B', true)} onClick={loadUsers}>Refresh</button>
        <span style={{ fontSize: '13px', color: '#64748B', alignSelf: 'center', marginLeft: 'auto' }}>
          {users.length} user{users.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* TABLE */}
      <div style={S.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>Loading users…</div>
        ) : users.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748B' }}>
            No users found.<br />
            <span style={{ fontSize: '13px' }}>Run the setup script first, or create a user above.</span>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Name', 'Email', 'Role', 'Status', 'Last Login', 'Created', 'Actions'].map(h => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} style={{ background: u.is_active ? '#fff' : '#FAFAFA' }}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 600 }}>{u.full_name}</div>
                    {u.phone && <div style={{ fontSize: '12px', color: '#94A3B8' }}>{u.phone}</div>}
                  </td>
                  <td style={S.td}>{u.email}</td>
                  <td style={S.td}><Badge role={u.role} /></td>
                  <td style={S.td}><StatusDot active={u.is_active} /></td>
                  <td style={S.td}>{formatDate(u.last_login)}</td>
                  <td style={S.td}>{formatDate(u.created_at)}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button style={{ ...S.btn('#1B3A6B', true), padding: '5px 10px', fontSize: '12px' }} onClick={() => openEdit(u)}>Edit</button>
                      <button style={{ ...S.btn('#C8920A', true), padding: '5px 10px', fontSize: '12px' }} onClick={() => openResetPassword(u)}>Reset PW</button>
                      <button
                        style={{ ...S.btn(u.is_active ? '#6B3A1B' : '#1B6B3A', true), padding: '5px 10px', fontSize: '12px' }}
                        onClick={() => handleToggleActive(u)}
                      >{u.is_active ? 'Deactivate' : 'Activate'}</button>
                      <button style={{ ...S.btn('#B91C1C', true), padding: '5px 10px', fontSize: '12px' }} onClick={() => setModal({ type: 'confirm_delete', user: u })}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── MODALS ─────────────────────────────────────────────────────────── */}

      {/* CREATE USER */}
      {modal.type === 'create' && (
        <div style={S.overlay}>
          <div style={S.modalBox}>
            <h2 style={{ ...S.title, fontSize: '20px', marginBottom: '24px' }}>Add New User</h2>
            <FieldRow label="Full Name *">
              <input style={S.input} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="e.g. Teresa Johnson" />
            </FieldRow>
            <FieldRow label="Email Address *">
              <input style={S.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="teresa@lifeandwealthsolutions.online" />
            </FieldRow>
            <FieldRow label="Role *">
              <select style={S.input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as any }))}>
                <option value="admin">Admin — Full platform access + user management</option>
                <option value="agent12">Agent 12 — HITL authority, policy binding</option>
                <option value="viewer">Viewer — Read-only access</option>
              </select>
            </FieldRow>
            <FieldRow label="Phone (optional)">
              <input style={S.input} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 770 555 0100" />
            </FieldRow>
            <FieldRow label="Temporary Password *">
              <input style={S.input} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 8 chars, uppercase, lowercase, digit" />
            </FieldRow>
            <FieldRow label="Confirm Password *">
              <input style={S.input} type="password" value={form.confirm_password} onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))} placeholder="Repeat password" />
            </FieldRow>
            <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '20px', padding: '10px', background: '#F8FAFC', borderRadius: '6px' }}>
              ⚠️ Share this password securely (Signal, not email). The user should change it on first login.
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={S.btn('#64748B', true)} onClick={closeModal}>Cancel</button>
              <button style={S.btn()} onClick={handleCreate} disabled={saving}>{saving ? 'Creating…' : 'Create User'}</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT USER */}
      {modal.type === 'edit' && modal.user && (
        <div style={S.overlay}>
          <div style={S.modalBox}>
            <h2 style={{ ...S.title, fontSize: '20px', marginBottom: '8px' }}>Edit User</h2>
            <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '24px' }}>{modal.user.email}</p>
            <FieldRow label="Full Name">
              <input style={S.input} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
            </FieldRow>
            <FieldRow label="Role">
              <select style={S.input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as any }))}>
                <option value="admin">Admin</option>
                <option value="agent12">Agent 12</option>
                <option value="viewer">Viewer</option>
              </select>
            </FieldRow>
            <FieldRow label="Phone (optional)">
              <input style={S.input} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </FieldRow>
            <FieldRow label="License Number (optional)">
              <input style={S.input} value={form.license_number} onChange={e => setForm(f => ({ ...f, license_number: e.target.value }))} />
            </FieldRow>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' }}>
              <button style={S.btn('#64748B', true)} onClick={closeModal}>Cancel</button>
              <button style={S.btn()} onClick={handleEdit} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}

      {/* RESET PASSWORD */}
      {modal.type === 'reset_password' && modal.user && (
        <div style={S.overlay}>
          <div style={S.modalBox}>
            <h2 style={{ ...S.title, fontSize: '20px', marginBottom: '8px' }}>Reset Password</h2>
            <p style={{ color: '#64748B', fontSize: '14px', marginBottom: '24px' }}>
              Resetting password for: <strong>{modal.user.full_name}</strong> ({modal.user.email})
            </p>
            <FieldRow label="New Password">
              <input
                style={S.input} type="password" value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Min 8 chars, uppercase, lowercase, digit"
              />
            </FieldRow>
            <div style={{ fontSize: '12px', color: '#64748B', marginBottom: '20px', padding: '10px', background: '#FFF9C4', borderRadius: '6px' }}>
              ⚠️ This resets the password immediately. Share the new password via Signal — never email or Slack.
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={S.btn('#64748B', true)} onClick={closeModal}>Cancel</button>
              <button style={S.btn('#C8920A')} onClick={handleResetPassword} disabled={saving}>{saving ? 'Resetting…' : 'Reset Password'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE */}
      {modal.type === 'confirm_delete' && modal.user && (
        <div style={S.overlay}>
          <div style={{ ...S.modalBox, width: '420px' }}>
            <h2 style={{ ...S.title, fontSize: '20px', color: '#B91C1C', marginBottom: '16px' }}>Delete User?</h2>
            <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', marginBottom: '24px' }}>
              You are about to permanently delete <strong>{modal.user.full_name}</strong> ({modal.user.email}).<br /><br />
              This cannot be undone. All their data and access will be removed immediately.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button style={S.btn('#64748B', true)} onClick={closeModal}>Cancel</button>
              <button style={S.btn('#B91C1C')} onClick={handleDelete} disabled={saving}>{saving ? 'Deleting…' : 'Yes, Delete Permanently'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
